import { ExamCourseLanguage, ExamCourseType } from '@exam-center/graphql';

import defaultTemplate from '../assets/certificates/default-template.svg';

/**
 * Template type - either for passed or failed certificates
 */
export type CertificateTemplateType = 'pass' | 'fail';

/**
 * Template value types
 */
export type TemplateValue =
  | 'average-score-by-part'
  | 'average-score-by-subject'
  | 'no-score'
  | 'section-score';

/**
 * Template definition with value, label, and conditions for each course type and language
 */
export interface TemplateDefinition {
  value: TemplateValue;
  label: string;
  conditions: {
    pass: {
      [ExamCourseType.Main]: {
        [ExamCourseLanguage.Th]: boolean;
        [ExamCourseLanguage.En]: boolean;
      };
      [ExamCourseType.Mock]: {
        [ExamCourseLanguage.Th]: boolean;
        [ExamCourseLanguage.En]: boolean;
      };
    };
    fail: {
      [ExamCourseType.Main]: {
        [ExamCourseLanguage.Th]: boolean;
        [ExamCourseLanguage.En]: boolean;
      };
      [ExamCourseType.Mock]: {
        [ExamCourseLanguage.Th]: boolean;
        [ExamCourseLanguage.En]: boolean;
      };
    };
  };
}

/**
 * Centralized configuration for all certificate templates
 */
export const certificateTemplates: TemplateDefinition[] = [
  {
    value: 'average-score-by-part',
    label: 'Average Score (รายหมวด)',
    conditions: {
      pass: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
      fail: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
    },
  },
  {
    value: 'average-score-by-subject',
    label: 'Average Score (รายวิชา)',
    conditions: {
      pass: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
      fail: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
    },
  },
  {
    value: 'no-score',
    label: 'No Score',
    conditions: {
      pass: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: true,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: false,
          [ExamCourseLanguage.En]: false,
        },
      },
      fail: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: true,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: false,
          [ExamCourseLanguage.En]: false,
        },
      },
    },
  },
  {
    value: 'section-score',
    label: 'Section Score',
    conditions: {
      pass: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: true,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
      fail: {
        [ExamCourseType.Main]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: true,
        },
        [ExamCourseType.Mock]: {
          [ExamCourseLanguage.Th]: true,
          [ExamCourseLanguage.En]: false,
        },
      },
    },
  },
];

export type Environment = 'development' | 'production' | 'staging' | 'uat';

/**
 * Configuration for Google Cloud Storage buckets
 */
export const gcsBucketConfig = {
  development: 'develop-set-asset-public',
  staging: 'staging-set-asset-public',
  uat: 'uat-set-asset-public',
  production: 'prod-set-asset-public',
} as const;

/**
 * Base path for certificate templates in GCS
 */
export const certificateBasePath = 'exam-center/certificate-templates';

export type CertificateImageSize = 'preview' | 'full';

/**
 * Map of certificate templates to their corresponding paths in GCS
 * The actual URL will be constructed at runtime based on the environment
 */
export const certificateTemplateImages = {
  default: defaultTemplate,
  pass: {
    [ExamCourseType.Main]: {
      'average-score-by-part': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/main/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'average-score-by-subject': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/main/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'no-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/main/no-score`,
        [ExamCourseLanguage.En]: `${certificateBasePath}/pass/main/no-score`,
      },
      'section-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/main/section-score`,
        [ExamCourseLanguage.En]: `${certificateBasePath}/pass/main/section-score`,
      },
    },
    [ExamCourseType.Mock]: {
      'average-score-by-part': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/mock/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'average-score-by-subject': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/mock/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'no-score': {
        [ExamCourseLanguage.Th]: undefined,
        [ExamCourseLanguage.En]: undefined,
      },
      'section-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/pass/mock/section-score`,
        [ExamCourseLanguage.En]: undefined,
      },
    },
  },
  fail: {
    [ExamCourseType.Main]: {
      'average-score-by-part': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/main/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'average-score-by-subject': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/main/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'no-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/main/no-score`,
        [ExamCourseLanguage.En]: `${certificateBasePath}/fail/main/no-score`,
      },
      'section-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/main/section-score`,
        [ExamCourseLanguage.En]: `${certificateBasePath}/fail/main/section-score`,
      },
    },
    [ExamCourseType.Mock]: {
      'average-score-by-part': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/mock/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'average-score-by-subject': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/mock/average-score`,
        [ExamCourseLanguage.En]: undefined,
      },
      'no-score': {
        [ExamCourseLanguage.Th]: undefined,
        [ExamCourseLanguage.En]: undefined,
      },
      'section-score': {
        [ExamCourseLanguage.Th]: `${certificateBasePath}/fail/mock/section-score`,
        [ExamCourseLanguage.En]: undefined,
      },
    },
  },
};

/**
 * Get certificate templates based on type, language, and whether it's for pass or fail
 */
export function getCertificateTemplates(
  courseType: ExamCourseType,
  language: ExamCourseLanguage,
  templateType: CertificateTemplateType,
) {
  return certificateTemplates
    .filter((template) => template.conditions[templateType][courseType][language])
    .map(({ value, label }) => ({ value, label }));
}

/**
 * Constructs a GCS URL for a certificate template
 *
 * @param environment The current environment (development, staging, uat, production)
 * @param path The base path to the certificate template in GCS
 * @param language The language of the certificate (Th or En)
 * @param size The size of the certificate image (preview or full)
 *
 * @returns The full URL to the certificate template
 */
export function constructGcsUrl(
  environment: Environment,
  path: string,
  language: ExamCourseLanguage,
  size: CertificateImageSize = 'preview',
): string {
  const bucket = gcsBucketConfig[environment];
  const languageCode = language === ExamCourseLanguage.Th ? 'th' : 'en';
  const filename = `${size}-${languageCode}.png`;
  return `https://storage.googleapis.com/${bucket}/${path}/${filename}`;
}

/**
 * Get the certificate template image based on course type, template value, and language
 *
 * @param templateType The type of the template (pass or fail)
 * @param courseType The type of the course (Main or Mock)
 * @param language The language of the certificate (Th or En)
 * @param templateValue The template value (average-score, no-score, section-score)
 * @param environment The current environment (development, staging, uat, production)
 * @param size The size of the certificate image (preview or full)
 *
 * @returns The URL to the certificate PNG image
 */
export function getCertificateTemplateImage(
  templateType: CertificateTemplateType,
  courseType: ExamCourseType,
  language: ExamCourseLanguage,
  templateValue: TemplateValue,
  environment: Environment = 'development',
  size: CertificateImageSize = 'preview',
) {
  try {
    const templatePath =
      certificateTemplateImages[templateType][courseType][templateValue][language];

    if (!templatePath) {
      return certificateTemplateImages.default;
    }

    return constructGcsUrl(environment, templatePath, language, size);
  } catch {
    return certificateTemplateImages.default;
  }
}
